import { Redirect, Route, useHistory, useLocation } from "react-router-dom";
import { IonApp, IonContent, IonPage, setupIonicReact } from "@ionic/react";
import routes from "./utils/routes.json";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Theme variables */
import "./theme/variables.css";

import { Suspense, lazy, useEffect } from "react";
import MuiThemeProvider from "./contexts/MuiThemeProvider";
import Loader from "./components/Loaders";
import SideNavigation from "./components/SideNavigation";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { userDispatch } from "./utils/localStorage";
import { ApiConfigs, addRouteHistory, PrivateRouter, PublicRouter } from "./utils/helpers";
import UserContextProvider from "./contexts/UserContextProvider";
import SearchContextProvider from "./contexts/SearchContextProvider";
import { FileUploadContext } from "./contexts/FileUploadContext";

const Login = lazy(() => import("./pages/Login"));
const SignUp = lazy(() => import("./pages/Signup"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const OtpVerification = lazy(() => import("./pages/OtpVerification"));
const Facilities = lazy(() => import("./pages/Facilities"));
const MeetingRooms = lazy(() => import("./pages/MeetingRooms"));
const FacilityDetails = lazy(() => import("./pages/FacilityDetails"));
const BookingFacility = lazy(() => import("./pages/BookFacility"));
const FacilitiesCart = lazy(() => import("./pages/FacilitiesCart"));
const FacilitiesBilling = lazy(() => import("./pages/FacilitiesCart/components/FacilitiesCartBilling"));
const Bookings = lazy(() => import("./pages/Bookings"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Notifications = lazy(() => import("./pages/Notifications"));
const CalendarEvents = lazy(() => import("./pages/Calendar"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const MembershipOnboard = lazy(() => import("./pages/MembershipOnboard"));
const AccessCardRequest = lazy(() => import("./pages/AccessCardRequest"));

setupIonicReact();

const App: React.FunctionComponent = () => {
  const navigate = useHistory();
  const { pathname, search } = useLocation();

  // Axios configuration Setup
  useEffect(() => {
    const getAccesssToken: () => void = async () => {
      const token = await userDispatch({ case: "GET", key: "token" });
      if (token) {
        ApiConfigs(token?.accessToken, token?.refreshToken, navigate);
      } else {
        // Route history adding
        const newroute = pathname + search
        await addRouteHistory(newroute);
      }
    };

    getAccesssToken();
  }, []);

  return (
    <IonApp>
      <MuiThemeProvider>
        <FileUploadContext>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <UserContextProvider>
              <SearchContextProvider>
                <Suspense fallback={<Loader />}>
                  <Route
                    exact
                    path={routes.login}
                    render={() => {
                      return (
                        <PublicRouter>
                          <IonPage>
                            <IonContent>
                              <Login />
                            </IonContent>
                          </IonPage>
                        </PublicRouter>
                      );
                    }}
                  />
                  <Route
                    exact
                    path={routes.membership}
                    render={() => {
                      return (
                        <PublicRouter>
                          <IonPage>
                            <IonContent>
                              <MembershipOnboard />
                            </IonContent>
                          </IonPage>
                        </PublicRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.otpVerify}
                    render={() => {
                      return (
                        <PublicRouter>
                          <IonPage>
                            <IonContent>
                              <OtpVerification />
                            </IonContent>
                          </IonPage>
                        </PublicRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.signup}
                    render={() => {
                      return (
                        <PublicRouter>
                          <IonPage>
                            <SideNavigation>
                              <SignUp />
                            </SideNavigation>
                          </IonPage>
                        </PublicRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.changePassword}
                    render={() => {
                      return (
                        <PublicRouter>
                          <IonPage>
                            <SideNavigation>
                              <ForgotPassword />
                            </SideNavigation>
                          </IonPage>
                        </PublicRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.meetings}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <MeetingRooms />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.facilitiesCart}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <FacilitiesCart />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.bookingFacility}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <BookingFacility />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.cartBilling}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <FacilitiesBilling />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={`${routes.facilityDetails}/:roomId`}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <FacilityDetails />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.facilities}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <Facilities />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.myProfile}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <MyProfile />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.bookings}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <Bookings />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.transactions}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <Transactions />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.notifications}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <Notifications />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.calendar}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            <SideNavigation>
                              <CalendarEvents />
                            </SideNavigation>
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route
                    path={routes.accessCardRequest}
                    render={() => {
                      return (
                        <PrivateRouter>
                          <IonPage>
                            {/* <SideNavigation> */}
                              <AccessCardRequest />
                            {/* </SideNavigation> */}
                          </IonPage>
                        </PrivateRouter>
                      );
                    }}
                  />
                  <Route exact path={"/"}>
                    <Redirect to={routes.login} />
                  </Route>
                </Suspense>
              </SearchContextProvider>
            </UserContextProvider>
          </LocalizationProvider>
        </FileUploadContext>
      </MuiThemeProvider>
    </IonApp>
  );
};

export default App;
